import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithCustomToken,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import {
  ApiError,
  apiPost,
  isTwoFactorEnrollmentRequiredError,
  isTwoFactorRequiredError,
  onTwoFactorEnrollmentRequired,
  onTwoFactorRequired,
} from '../lib/apiClient';
import { getMsalInstance } from '../lib/msalClient';
import { requestGoogleAccessToken } from '../lib/googleIdentity';
import { isPortalRole, UserRole } from '../lib/roles';
import { getDevicePublicKey } from '../lib/payloadCrypto';

interface PortalUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  /**
   * Per-user permission overrides, as stored on the Firestore user doc — /auth/sync already
   * returns this (IUser.permissions), no backend change needed for ADM-24. Used only for the
   * portal's own client-side nav/route gating (ProtectedRoute's requiredPermission); the
   * backend's PermissionsGuard is the real enforcement point and additionally unions in the
   * role's live effective permissions (RolePermissionsService), so this can lag it briefly
   * after a role-level permission edit — tracked as ADM-30.
   */
  permissions?: string[];
}

interface SyncFirebaseResponse {
  user: PortalUser;
}

// 'two-factor-required': signed in with Firebase, but the backend's login-time 2FA gate
// (FirebaseAuthGuard) refuses every route until POST /auth/2fa/verify succeeds and the ID token
// carries the new `tfaVerifiedAt` claim. RequireAuth renders the challenge in this state.
// 'two-factor-enrollment-required': the backend's admin 2FA enrollment gate (default-OFF switch)
// refuses every route with 403 TWO_FACTOR_ENROLLMENT_REQUIRED until this account has set up and
// confirmed 2FA. RequireAuth renders the enrollment screen in this state.
type AuthStatus =
  | 'loading'
  | 'signed-out'
  | 'unauthorized'
  | 'authorized'
  | 'two-factor-required'
  | 'two-factor-enrollment-required';

/** What the enrollment screen needs to show: the backend's QR data URL and manual-entry secret. */
export interface TwoFactorSetupData {
  qrCodeDataUrl: string;
  secret: string;
}

interface AuthContextValue {
  status: AuthStatus;
  firebaseUser: FirebaseUser | null;
  portalUser: PortalUser | null;
  signInWithGoogle: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Submit the login-time 2FA code (TOTP or backup code). Resolves once the backend accepted
   * it, the ID token was force-refreshed and the app resumed; throws ApiError otherwise.
   */
  verifyTwoFactor: (code: string) => Promise<void>;
  /** Enrollment step 1: `POST /auth/2fa/setup`. Returns the QR data URL and the manual secret. */
  startTwoFactorSetup: () => Promise<TwoFactorSetupData>;
  /**
   * Enrollment step 2: `POST /auth/2fa/verify` with the first authenticator code (which enables
   * 2FA) and force-refresh of the ID token so it carries the new proof claim. Resolves with the
   * ONE-TIME plaintext backup codes. Does NOT resume the app: the screen keeps showing the codes
   * until the user acknowledges them and `finishTwoFactorEnrollment` is called.
   */
  completeTwoFactorEnrollment: (code: string) => Promise<string[]>;
  /** Enrollment step 3: the user confirmed they saved the backup codes; resume the app. */
  finishTwoFactorEnrollment: () => void;
  error: string | null;
}

interface TwoFactorVerifyResponse {
  success?: boolean;
  backupCodes?: string[];
}

interface TwoFactorSetupResponse {
  secret?: string;
  qrCodeDataUrl?: string;
  // The backend also sends provisional backupCodes here; they are never used (verify returns
  // the real final set), so they are deliberately not even typed.
}

interface MicrosoftExchangeResponse {
  customToken: string;
}

interface GoogleExchangeResponse {
  customToken: string;
}

// FirebaseError's own .message is often just "Firebase: Error (auth/internal-error)." with
// no detail — .code/.customData carry the actually-useful part but the app was swallowing
// them, which is why debugging the redirect-completion failure needed browser DevTools
// instead of just reading the on-screen error. Surface everything available instead.
const describeAuthError = (err: unknown): string => {
  if (err && typeof err === 'object' && 'code' in err) {
    const fbErr = err as { code?: string; message?: string; customData?: unknown };
    const parts = [fbErr.code, fbErr.message].filter(Boolean);
    if (fbErr.customData && Object.keys(fbErr.customData as object).length > 0) {
      parts.push(JSON.stringify(fbErr.customData));
    }
    return parts.join(' — ');
  }
  // ApiError (lib/apiClient.ts) — thrown by /auth/sync when the backend rejects the token.
  if (err && typeof err === 'object' && 'statusCode' in err) {
    const apiErr = err as { statusCode?: number; message?: string; errors?: unknown };
    const parts = [apiErr.statusCode != null ? `HTTP ${apiErr.statusCode}` : undefined, apiErr.message].filter(
      Boolean,
    );
    if (apiErr.errors) parts.push(JSON.stringify(apiErr.errors));
    return parts.join(' — ');
  }
  return err instanceof Error ? err.message : 'Sign-in failed';
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [portalUser, setPortalUser] = useState<PortalUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped after a successful 2FA verify when the profile bootstrap itself was the request the
  // gate refused: re-subscribing re-runs /auth/sync for the current user without a page reload.
  const [bootstrapNonce, setBootstrapNonce] = useState(0);

  // Any authenticated request the backend refuses with 403 TWO_FACTOR_REQUIRED flips the whole
  // portal into the challenge. Other 403s (missing permission, etc.) never reach this.
  useEffect(
    () =>
      onTwoFactorRequired(() =>
        setStatus((prev) => (prev === 'authorized' ? 'two-factor-required' : prev)),
      ),
    [],
  );

  // Same for the enrollment gate: 403 TWO_FACTOR_ENROLLMENT_REQUIRED on any authenticated
  // request flips the portal into the enrollment screen. Inert until the backend sends it.
  useEffect(
    () =>
      onTwoFactorEnrollmentRequired(() =>
        setStatus((prev) => (prev === 'authorized' ? 'two-factor-enrollment-required' : prev)),
      ),
    [],
  );

  useEffect(() => {
    // Server already enforces roles via FirebaseAuthGuard/RolesGuard on every endpoint —
    // this client-side check exists only so a rejected role never even sees the menu
    // (ADM-03), not as the actual security boundary.
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);

      if (!user) {
        setPortalUser(null);
        setStatus('signed-out');
        return;
      }

      try {
        const idToken = await user.getIdToken();
        const publicKey = getDevicePublicKey();
        const { user: syncedUser } = await apiPost<SyncFirebaseResponse>('/auth/sync', {
          idToken,
          ...(publicKey ? { publicKey, publicKeyAlgo: 'x25519' } : {}),
        });

        if (!isPortalRole(syncedUser.role)) {
          setError(`Account role "${syncedUser.role}" is not authorized for the admin portal. Only ADMIN, SUPPORT, and LEAD_SPECIALIST roles can log in.`);
          setPortalUser(null);
          setStatus('unauthorized');
          await firebaseSignOut(auth);
          return;
        }

        setPortalUser(syncedUser);
        setStatus('authorized');
      } catch (err) {
        if (isTwoFactorRequiredError(err)) {
          // The gate refused the bootstrap itself: keep the Firebase session and ask for the code.
          setStatus('two-factor-required');
          return;
        }
        if (isTwoFactorEnrollmentRequiredError(err)) {
          // Same for the enrollment gate: keep the session and let the user enroll.
          setStatus('two-factor-enrollment-required');
          return;
        }
        // Was console-only before — a failed /auth/sync after a successful Google/Microsoft
        // sign-in fell straight through to firebaseSignOut() below, which re-fires this same
        // listener with user=null and overwrites status to 'signed-out' before RequireAuth
        // ever renders the 'unauthorized' redirect. Net effect: silently bounced back to
        // /login with zero visible error. `error` state isn't touched by that re-fire, so
        // setting it here is what actually survives long enough to reach the screen.
        console.error('[AuthContext] Failed to sync user with backend:', err);
        setError(describeAuthError(err));
        setPortalUser(null);
        setStatus('unauthorized');
        await firebaseSignOut(auth);
      }
    });

    return unsubscribe;
  }, [bootstrapNonce]);

  // Neither of Firebase's own browser flows worked in production (see ADM-33 / PR history):
  // signInWithPopup needs third-party storage access for its iframe relay, which Chrome's
  // default storage partitioning blocks (auth/internal-error, zero popup, zero network
  // activity — confirmed 2026-08-29). signInWithRedirect got further — it reached Google's
  // real consent screen — but getRedirectResult() then silently resolved to null after the
  // app.mediclynk.com -> accounts.google.com -> mediclink-bb009.firebaseapp.com/__/auth/
  // handler -> back round trip: no exception anywhere, just a lost credential. Both failure
  // modes trace to the same cause — Firebase's iframe-based cross-origin relay needing
  // storage access that's no longer reliably available.
  //
  // This sidesteps that relay entirely: Google Identity Services (src/lib/googleIdentity.ts)
  // gets a real Google access token via its own popup + postMessage channel — Google's own,
  // never touching Firebase's — then the backend verifies it and mints a Firebase custom
  // token (POST /auth/google/exchange), same shape as the Microsoft flow below.
  const signInWithGoogle = async () => {
    setError(null);
    try {
      const accessToken = await requestGoogleAccessToken();
      const { customToken } = await apiPost<GoogleExchangeResponse>(
        '/auth/google/exchange',
        { accessToken },
        { requiresAuth: false },
      );
      await signInWithCustomToken(auth, customToken);
    } catch (err) {
      setError(describeAuthError(err));
      throw err;
    }
  };

  // Mirrors app/services/firebaseAuth.ts's signInWithMicrosoft: get a Microsoft ID token,
  // exchange it for a Firebase custom token via the same backend endpoint
  // (POST /auth/microsoft/exchange) the mobile app uses, then sign in with that token —
  // there's no native Firebase "microsoft.com" popup provider in this project.
  const signInWithMicrosoft = async () => {
    setError(null);
    try {
      const msal = await getMsalInstance();
      const result = await msal.loginPopup({ scopes: ['openid', 'profile', 'email'] });
      if (!result.idToken) throw new Error('No Microsoft ID token returned.');

      const { customToken } = await apiPost<MicrosoftExchangeResponse>(
        '/auth/microsoft/exchange',
        { idToken: result.idToken },
        { requiresAuth: false },
      );
      await signInWithCustomToken(auth, customToken);
    } catch (err) {
      setError(describeAuthError(err));
      throw err;
    }
  };

  const signOut = async () => {
    setError(null);
    await firebaseSignOut(auth);
    setPortalUser(null);
    setStatus('signed-out');
  };

  const verifyTwoFactor = async (code: string) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new ApiError(401, 'Not signed in');
    // /auth/2fa/verify is @SkipTwoFactorCheck, so it is reachable before verification.
    const result = await apiPost<TwoFactorVerifyResponse>('/auth/2fa/verify', { code });
    if (result?.success === false) {
      throw new ApiError(400, 'Invalid verification code');
    }
    // The backend synced `tfaVerifiedAt` into the custom claims; the cached ID token predates
    // it, so force a refresh before anything retries.
    await currentUser.getIdToken(true);
    setError(null);
    if (portalUser) {
      // Layout and the section pages were unmounted while the challenge showed; remounting
      // them re-issues the requests that were refused, now with the verified token.
      setStatus('authorized');
    } else {
      setBootstrapNonce((n) => n + 1);
    }
  };

  const startTwoFactorSetup = async (): Promise<TwoFactorSetupData> => {
    if (!auth.currentUser) throw new ApiError(401, 'Not signed in');
    const result = await apiPost<TwoFactorSetupResponse>('/auth/2fa/setup');
    if (!result?.qrCodeDataUrl || !result.secret) {
      throw new ApiError(502, 'Incomplete two-factor setup response');
    }
    return { qrCodeDataUrl: result.qrCodeDataUrl, secret: result.secret };
  };

  const completeTwoFactorEnrollment = async (code: string): Promise<string[]> => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new ApiError(401, 'Not signed in');
    // The first successful verify after setup enables 2FA and returns the one-time backup codes.
    const result = await apiPost<TwoFactorVerifyResponse>('/auth/2fa/verify', { code });
    if (result?.success === false) {
      throw new ApiError(400, 'Invalid verification code');
    }
    // The backend synced the proof claim into the custom claims; the cached ID token predates
    // it, so force a refresh before the app resumes.
    await currentUser.getIdToken(true);
    return Array.isArray(result?.backupCodes) ? result.backupCodes : [];
  };

  const finishTwoFactorEnrollment = () => {
    setError(null);
    if (portalUser) {
      // Layout and the section pages were unmounted while the enrollment showed; remounting
      // them re-issues the refused requests, now with the refreshed token.
      setStatus('authorized');
    } else {
      // The profile bootstrap itself was refused: re-run /auth/sync for the current user.
      setBootstrapNonce((n) => n + 1);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        status,
        firebaseUser,
        portalUser,
        signInWithGoogle,
        signInWithMicrosoft,
        signOut,
        verifyTwoFactor,
        startTwoFactorSetup,
        completeTwoFactorEnrollment,
        finishTwoFactorEnrollment,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
